import { defineStore } from "pinia";
import { useDocumentStore } from "../../../eew/src/stores/documents";
import {
  LoadState,
  PaginatedLoadState,
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
} from "@shared/models/state";
import { reactive, ref } from "vue";
import { Paginated, PaginationQuery } from "@shared/services/common";
import {
  DocumentsQuery,
  DocumentsSearch,
  FileMetaData,
  FileMetaDataPatch,
  deleteDocument,
  getDocuments,
  patchDocument,
  postDocument,
  searchDocuments,
} from "@shared/services/documents";
import { AxiosProgressEvent } from "axios";
import { PagingQuery } from "@shared/services/signals";
import { loadPaginatedResource } from "@shared/util/loadResource";

export const useTwinDocumentsStore = defineStore("assetDocuments", () => {
  const twinImageLoadingState = ref<
    Map<string, PaginatedLoadState<FileMetaData[]>>
  >(new Map());
  const twinDocumentsLoadingState = ref<PaginatedLoadState<FileMetaData[]>>({
    ...stateInitial,
  });

  const currentVideoPage = ref({ page: 1, pageSize: 20 });

  function getTwinImageLoadingState(twinId: string) {
    return (
      twinImageLoadingState.value.get(twinId) || { ...stateInitial, result: [] }
    );
  }

  async function loadTwinImages(twinId: string, reload = true) {
    const oldValue = getTwinImageLoadingState(twinId);
    if (oldValue?.loaded && !reload) {
      return;
    }
    twinImageLoadingState.value.set(twinId, {
      ...oldValue,
      ...stateLoading,
    });
    // We set a maximum at 100 images
    const response = await searchDocuments(
      { page: 1, pageSize: 100 },
      {
        type: "image",
        category: "twinImage",
        entityType: "twin",
        entityId: twinId,
      },
    ).catch((e) => {
      twinImageLoadingState.value.set(twinId, {
        ...oldValue,
        ...stateError,
      });
      throw e;
    });
    twinImageLoadingState.value.set(twinId, {
      ...response.data,
      page: { page: 1, pageSize: 100 },
      ...stateLoaded,
    });
  }

  async function addTwinImages(twinId: string, fileList: FileList) {
    if (fileList.length === 0) {
      return;
    }
    // The first image to be uploaded should have purpose: "primary" set
    const hasImages =
      (getTwinImageLoadingState(twinId).result ?? []).length > 0;

    const responses = await Promise.all(
      Array.from(fileList).map((file, index) => {
        const formData = new FormData();
        formData.append("name", file.name);
        formData.append("category", "twinImage");
        formData.append("type", "image");
        formData.append("entityType", "twin");
        formData.append("entityId", twinId);
        if (hasImages === false && index === 0) {
          formData.append("purpose", "primary");
        }
        formData.append("file", file, file.name);
        return postDocument(formData, () => {});
      }),
    );
    loadTwinImages(twinId, true);
    return responses;
  }

  async function deleteTwinImage(twinId: string, imageId: string) {
    const response = await deleteDocument(imageId);
    loadTwinImages(twinId, true);
    return response;
  }

  async function setTwinImagePrimary(
    entity: {
      entityId: string;
      entityType: string;
    },
    imageId: string,
  ) {
    // NOTE; the backend does currently not natively supprt primary images, thus we have to check if another image is primary and unset its primary status first
    const images = getTwinImageLoadingState(entity.entityId).result ?? [];
    const previousPrimary = images.find((item) => item.purpose === "primary");
    if (previousPrimary !== undefined) {
      await patchDocument(previousPrimary.uuid, entity, {
        purpose: "",
      });
    }
    const response = await patchDocument(imageId, entity, {
      purpose: "primary",
    });
    loadTwinImages(entity.entityId, true);
  }

  async function loadTwinDocuments(twinId: string, page: PaginationQuery) {
    twinDocumentsLoadingState.value = {
      ...twinDocumentsLoadingState.value,
      ...stateLoading,
    };
    const response = await searchDocuments(page, {
      category: "twinDocument",
      type: "document",
      entityType: "twin",
      entityId: twinId,
    }).catch((e) => {
      twinDocumentsLoadingState.value = {
        ...twinDocumentsLoadingState.value,
        ...stateError,
      };
      throw e;
    });

    twinDocumentsLoadingState.value = {
      ...stateLoaded,
      ...response.data,
      page,
    };
  }

  async function addTwinDocument(
    twinId: string,
    fileList: FileList,
    metadata: { description: string; category?: string },
    onUploadProgress: (event: AxiosProgressEvent) => void,
  ) {
    if (fileList.length === 0) {
      return;
    }
    const file = fileList[0];
    const formData = new FormData();
    formData.append("name", file.name);
    formData.append("category", metadata.category ?? "twinDocument");
    formData.append("type", "document");
    formData.append("entityType", "twin");
    formData.append("entityId", twinId);
    formData.append("description", metadata.description);
    formData.append("file", file, file.name);
    const response = await postDocument(formData, onUploadProgress);
    if (twinDocumentsLoadingState.value.page) {
      loadTwinDocuments(twinId, twinDocumentsLoadingState.value.page);
    }
    reloadTwinDocumentsByCategory(twinId, metadata.category);
    return response;
  }

  async function patchTwinDocument(
    twinId: string,
    documentId: string,
    patch: FileMetaDataPatch,
    category?: string,
  ) {
    await patchDocument(
      documentId,
      { entityId: twinId, entityType: "twin" },
      patch,
    );
    if (category === undefined && twinDocumentsLoadingState.value.page) {
      loadTwinDocuments(twinId, twinDocumentsLoadingState.value.page);
    } else if (category) {
      reloadTwinDocumentsByCategory(twinId, category);
    }
  }

  async function deleteTwinDocument(
    twinId: string,
    documentId: string,
    category?: string,
  ) {
    twinDocumentsLoadingState.value = {
      ...twinDocumentsLoadingState.value,
      ...stateLoading,
    };
    const response = await deleteDocument(documentId).catch((error) => {
      loadTwinDocuments(twinId, twinDocumentsLoadingState.value.page);
      throw error;
    });
    if (category === undefined && twinDocumentsLoadingState.value.page) {
      loadTwinDocuments(twinId, twinDocumentsLoadingState.value.page);
    } else if (category) {
      reloadTwinDocumentsByCategory(twinId, category);
    }
    return response;
  }

  const documentsLoadingState = ref<
    Map<string, PaginatedLoadState<FileMetaData[]>>
  >(new Map());

  function toStateId(id: string, entity: string) {
    return `${entity}:${id}`;
  }

  async function loadDocuments(
    id: string,
    entity: string,
    page: PaginationQuery,
  ) {
    const stateId = toStateId(id, entity);
    const state = documentsLoadingState.value.get(stateId);
    documentsLoadingState.value.set(stateId, {
      ...stateLoading,
      page,
      result: state?.result || [],
    });

    twinDocumentsLoadingState.value = {
      ...twinDocumentsLoadingState.value,
      ...stateLoading,
    };
    const response = await searchDocuments(page, {
      category: "document",
      type: "document",
      entityType: entity,
      entityId: id,
    }).catch((e) => {
      documentsLoadingState.value.set(stateId, {
        ...stateError,
        page,
        result: state?.result || [],
      });
      throw e;
    });

    documentsLoadingState.value.set(stateId, {
      ...stateLoaded,
      ...response.data,
      page,
    });
  }

  async function addDocument(
    id: string,
    entity: string,
    fileList: FileList,
    metadata: { description: string },
    onUploadProgress: (event: AxiosProgressEvent) => void,
  ) {
    if (fileList.length === 0) {
      return;
    }
    const file = fileList[0];
    const formData = new FormData();
    formData.append("name", file.name);
    formData.append("category", "document");
    formData.append("type", "document");
    formData.append("entityType", entity);
    formData.append("entityId", id);
    formData.append("description", metadata.description);
    formData.append("file", file, file.name);
    const response = await postDocument(formData, onUploadProgress);

    const stateId = toStateId(id, entity);
    const loadState = documentsLoadingState.value.get(stateId);
    if (loadState !== undefined) {
      loadDocuments(id, entity, loadState.page);
    }

    return response;
  }

  async function updateDocument(
    id: string,
    entityId: string,
    documentId: string,
    patch: FileMetaDataPatch,
  ) {
    await patchDocument(
      documentId,
      { entityType: "twin", entityId: entityId },
      patch,
    );
    if (twinDocumentsLoadingState.value.page) {
      loadDocuments(id, entityId, twinDocumentsLoadingState.value.page);
    }
  }

  async function removeDocument(
    id: string,
    entity: string,
    documentId: string,
  ) {
    twinDocumentsLoadingState.value = {
      ...twinDocumentsLoadingState.value,
      ...stateLoading,
    };
    const response = await deleteDocument(documentId).catch((error) => {
      throw error;
    });
    const stateId = toStateId(id, entity);
    const loadState = documentsLoadingState.value.get(stateId);
    if (loadState !== undefined) {
      loadDocuments(id, entity, loadState.page);
    }
    return response;
  }

  function getDocumentsLoadingState(id: string, entity: string) {
    return documentsLoadingState.value.get(toStateId(id, entity));
  }

  // Twin documents by category
  const twinDocumentsByCategoryLoadState = ref<
    Map<string, PaginatedLoadState<FileMetaData[]>>
  >(new Map());

  async function loadTwinDocumentByCategory(
    category: string,
    page: PaginationQuery,
    query: DocumentsSearch,
  ) {
    const key = category ?? "" + query.entityId;

    twinDocumentsByCategoryLoadState.value.set(key, {
      ...(twinDocumentsByCategoryLoadState.value.get(key) || {}),
      ...stateLoading,
    });

    const response = await searchDocuments(page, query).catch((e) => {
      twinDocumentsByCategoryLoadState.value.set(key, { ...stateError });
      throw e;
    });

    twinDocumentsByCategoryLoadState.value.set(key, {
      ...stateLoaded,
      ...response.data,
      page,
      query,
    });
  }

  function reloadTwinDocumentsByCategory(twinId: string, category: string) {
    const loadState = getTwinDocumentsByCategory(twinId, category);
    if (loadState?.loaded) {
      loadTwinDocumentByCategory(category, loadState.page, loadState.query);
    }
  }

  // Workaround for https://github.com/vuejs/core/issues/12054
  function getTwinDocumentsByCategory(id: string, category?: string) {
    const key = category ?? "" + id;
    return twinDocumentsByCategoryLoadState.value.get(key);
  }

  function clearTwinDocumentsByCategory() {
    twinDocumentsByCategoryLoadState.value = new Map();
  }

  async function changeTwinDocumentCategory(
    twinId: string,
    document: FileMetaData,
    category: string,
  ) {
    const response = await patchDocument(
      document.uuid,
      {
        entityId: twinId,
        entityType: "twin",
      },
      {
        ...document,
        category: category !== undefined ? category : null,
      },
    );
    reloadTwinDocumentsByCategory(twinId, document.category);
    reloadTwinDocumentsByCategory(twinId, category);
  }

  return {
    twinImageLoadingState,
    getTwinImageLoadingState,
    loadTwinImages,
    addTwinImages,
    deleteTwinImage,
    setTwinImagePrimary,
    twinDocumentsLoadingState,
    loadTwinDocuments,
    patchTwinDocument,
    deleteTwinDocument,
    addTwinDocument,

    // General purpose documents
    getDocumentsLoadingState,
    loadDocuments,
    addDocument,
    updateDocument,
    removeDocument,

    // Twin Documents by category
    loadTwinDocumentByCategory,
    getTwinDocumentsByCategory,
    clearTwinDocumentsByCategory,
    changeTwinDocumentCategory,
  };
});
